import React, { useState, useEffect, useMemo, useRef } from 'react';
import { statusTint } from '../../lib/theme';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../auth/AuthContext';
import { useStore, useNav } from '../../store/StoreContext';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { FormField, Button, IconButton, inputCls, NumberField, ChoiceKeys, Switch, Skeleton, RowSkeleton, FormErrorBanner } from '../ui';
import { IconX, IconDollarSign, IconArrowRight, IconTruck, IconUsers, IconFileText, IconClipboard, IconCheck } from '../icons';
import EstimateExpandedDetails from '../forms/EstimateExpandedDetails';
import ActivityLog from '../ActivityLog';
import AssigneePicker from '../ui/AssigneePicker';
import { DEFAULT_ESTIMATE_STATUSES as ESTIMATE_STATUSES, isTransitionAllowed, TRUCK_TYPE_OPTIONS_COMPACT as TRUCK_TYPE_OPTIONS, estimateEffectiveRate, estimateExtraTruckSurcharge, estimateTruckLabel } from '../../lib/constants';
import { getEstimateStatus, lookupRate, lookupPackingRate } from '../../utils/';
import db from '../../lib/database';
import { formatPhone } from '../../lib/phone';
import { publicLink } from '../../lib/publicLinks';
import { useEstimateStatusChange } from '../../hooks/useEstimateStatusChange';
import { phaseLaborPricing, phaseHasLaborPricing } from '../../lib/phaseKinds';
import { moveForEstimate, fmtMoveDate } from '../../lib/moves';

// TRUCK_TYPE_OPTIONS is imported above from constants
// (TRUCK_TYPE_OPTIONS_COMPACT — the short-label flavor for this
// compact edit popover). Single source of truth in src/lib/constants.js.

export default function EstimateModal({ estimate, clients, crew, trucks, onClose, onSave, onDelete }) {
    const { can, user, displayName } = useAuth();
    const canViewPricing = can('view_estimate_pricing');
    const canEditPricing = can('edit_pricing');
    const { show: showToast } = useToast();
    const { navigateToClient } = useNav();
    const { config, loadAll, moves, confirmMoveDates, saveMove } = useStore();
    // The option's move (2026-09-18): dates live there; this legacy editor
    // only reads them, and hands the move to the Book confirm's date chips.
    const estMove = moveForEstimate(estimate, moves);
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    // Client name for confirm titles (2B: name the record).
    const confirmClientName = clients?.find(c => c.id === estimate?.client_id)?.name || 'this client';
    const [saving, setSaving] = useState(false);
    // 4B: refused pricing saves list their problems in a banner, not a toast.
    const [pricingErrors, setPricingErrors] = useState([]);
    const [editMode, setEditMode] = useState(false);
    // Status-change safeguard: confirmation dialog + warning logic now lives
    // in the shared useEstimateStatusChange hook so EstimateExpandedDetails
    // and any future status-change surface share the same rails.
    // `statusChanger.request(newStatus)` opens the dialog if a warning is
    // needed, or commits directly otherwise. `statusChanger.dialog` is the
    // portal-rendered confirmation modal (rendered at the bottom of this
    // component's JSX).
    const statusChanger = useEstimateStatusChange({
        currentStatus: estimate?.status,
        // Rule 4 (2026-09-18): booking never refuses on a tentative date —
        // the Book confirm shows the move's date chips instead, and the ✓
        // ones are logged with the booker's name.
        move: estMove,
        confirmDates: keys => confirmMoveDates(estMove.id, keys, displayName),
        commit: async (newStatus) => {
            await onSave({ ...estimate, status: newStatus });
            showToast(`Status → ${getEstimateStatus(newStatus).label}`);
        },
    });

    // ===== WRITTEN ESTIMATE (client-facing ballpark) STATE =====
    // NC legal note: sending a "written estimate" triggers pricing change
    // restrictions under state moving rules. We warn the user before generating
    // the link and surface the token + sent_at timestamp.
    const [writtenModal, setWrittenModal] = useState(false);       // "Send Written Estimate?" confirmation
    const [writtenLink, setWrittenLink] = useState(null);          // the hosted URL after generation
    const [writtenLinkSentAt, setWrittenLinkSentAt] = useState(null);
    const [writtenLinkAlreadySent, setWrittenLinkAlreadySent] = useState(false);
    const [writtenCopied, setWrittenCopied] = useState(false);
    const [writtenLoading, setWrittenLoading] = useState(false);

    // On mount / estimate change, preload the link if one was already generated
    useEffect(() => {
        if (estimate?.written_estimate_token) {
            setWrittenLink(publicLink('estimate', estimate.written_estimate_token));
            setWrittenLinkSentAt(estimate.written_estimate_sent_at || null);
            setWrittenLinkAlreadySent(true);
        } else {
            setWrittenLink(null);
            setWrittenLinkSentAt(null);
            setWrittenLinkAlreadySent(false);
        }
        setWrittenCopied(false);
    }, [estimate?.id, estimate?.written_estimate_token, estimate?.written_estimate_sent_at]);

    const handleGenerateWrittenLink = async () => {
        if (!estimate?.id) return;
        setWrittenLoading(true);
        try {
            const result = await db.generateWrittenEstimateLink(estimate.id);
            setWrittenLink(publicLink('estimate', result.token));
            setWrittenLinkSentAt(result.sentAt);
            setWrittenLinkAlreadySent(!!result.alreadySent);
            showToast(result.alreadySent ? 'Existing link loaded' : 'Written estimate link generated');
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to generate link'), 'error');
        } finally {
            setWrittenLoading(false);
        }
    };

    const handleCopyWrittenLink = async () => {
        if (!writtenLink) return;
        try {
            await navigator.clipboard.writeText(writtenLink);
            setWrittenCopied(true);
            setTimeout(() => setWrittenCopied(false), 2000);
        } catch {
            // Fallback: select text manually
            showToast('Copy failed — please select manually', 'error');
        }
    };

    // ===== DEPOSIT LINK STATE =====
    const [depositPresets, setDepositPresets] = useState([100, 250, 500, 1000]);
    const [depositAmountInput, setDepositAmountInput] = useState('');
    const [depositLink, setDepositLink] = useState(null);
    const [depositLinkAmount, setDepositLinkAmount] = useState(null);
    const [depositSentAt, setDepositSentAt] = useState(null);
    const [depositPaidAt, setDepositPaidAt] = useState(null);
    const [depositReceiptUrl, setDepositReceiptUrl] = useState(null);
    const [depositLast4, setDepositLast4] = useState(null);
    const [depositCardBrand, setDepositCardBrand] = useState(null);
    const [depositCopied, setDepositCopied] = useState(false);
    const [depositLoading, setDepositLoading] = useState(false);

    // Load presets once
    useEffect(() => {
        let cancelled = false;
        db.getDepositPresets().then(arr => { if (!cancelled && Array.isArray(arr) && arr.length) setDepositPresets(arr); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Preload deposit state from estimate on open / change
    useEffect(() => {
        if (estimate?.deposit_token) {
            setDepositLink(publicLink('deposit', estimate.deposit_token));
            setDepositLinkAmount(Number(estimate.deposit_amount || 0));
            setDepositAmountInput(estimate.deposit_amount != null ? String(estimate.deposit_amount) : '');
            setDepositSentAt(estimate.deposit_sent_at || null);
        } else {
            setDepositLink(null);
            setDepositLinkAmount(null);
            setDepositAmountInput('');
            setDepositSentAt(null);
        }
        setDepositPaidAt(estimate?.deposit_paid_at || null);
        setDepositReceiptUrl(estimate?.deposit_receipt_url || null);
        setDepositLast4(estimate?.deposit_last4 || null);
        setDepositCardBrand(estimate?.deposit_card_brand || null);
        setDepositCopied(false);
    }, [estimate?.id, estimate?.deposit_token, estimate?.deposit_amount, estimate?.deposit_sent_at, estimate?.deposit_paid_at, estimate?.deposit_receipt_url, estimate?.deposit_last4, estimate?.deposit_card_brand]);

    const handleGenerateDepositLink = async (amount) => {
        if (!estimate?.id) return;
        const amt = Number(amount);
        if (!(amt > 0)) { showToast('Enter a deposit amount greater than $0', 'error'); return; }
        setDepositLoading(true);
        try {
            const result = await db.generateDepositLink(estimate.id, amt);
            setDepositLink(publicLink('deposit', result.token));
            setDepositLinkAmount(result.amount);
            setDepositAmountInput(String(result.amount));
            setDepositSentAt(result.sentAt || new Date().toISOString());
            if (result.alreadyPaid) {
                showToast('This deposit was already paid — cannot regenerate', 'error');
            } else {
                showToast(result.alreadySent ? 'Existing deposit link loaded' : 'Deposit link generated');
            }
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to generate deposit link'), 'error');
        } finally {
            setDepositLoading(false);
        }
    };

    const handleCopyDepositLink = async () => {
        if (!depositLink) return;
        try {
            await navigator.clipboard.writeText(depositLink);
            setDepositCopied(true);
            setTimeout(() => setDepositCopied(false), 2000);
        } catch {
            showToast('Copy failed — please select manually', 'error');
        }
    };

    const handleClearDepositLink = async () => {
        if (!estimate?.id) return;
        if (depositPaidAt) { showToast('Cannot clear — deposit already paid', 'error'); return; }
        if (!(await confirmDialog({ title: `Clear ${confirmClientName}'s deposit link?`, body: `The deposit link already sent to ${confirmClientName} stops working. The estimate and its pricing are kept — you can generate a new link afterwards.`, confirmLabel: 'Clear deposit link', danger: true }))) return;
        setDepositLoading(true);
        try {
            await db.clearDepositLink(estimate.id);
            setDepositLink(null);
            setDepositLinkAmount(null);
            setDepositSentAt(null);
            showToast('Deposit link cleared');
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to clear'), 'error');
        } finally {
            setDepositLoading(false);
        }
    };

    // ===== TERMS OF SERVICE STATE =====
    // Independent of the written estimate token — staff can send terms on
    // their own (e.g. by text) without issuing a full written estimate.
    const [termsLink, setTermsLink] = useState(null);
    const [termsSentAt, setTermsSentAt] = useState(null);
    const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
    const [termsVersion, setTermsVersion] = useState(null);
    // 'signed' (direct clickwrap) | 'carried_forward' (auto-applied from client) | null
    const [termsSource, setTermsSource] = useState(null);
    const [termsCopied, setTermsCopied] = useState(false);
    const [termsLoading, setTermsLoading] = useState(false);

    // Preload terms state from estimate on open / change
    useEffect(() => {
        if (estimate?.terms_token) {
            setTermsLink(publicLink('terms', estimate.terms_token));
            setTermsSentAt(estimate.terms_sent_at || null);
        } else {
            setTermsLink(null);
            setTermsSentAt(null);
        }
        setTermsAcceptedAt(estimate?.terms_accepted_at || null);
        setTermsVersion(estimate?.terms_version || null);
        setTermsSource(estimate?.terms_source || null);
        setTermsCopied(false);
    }, [estimate?.id, estimate?.terms_token, estimate?.terms_sent_at, estimate?.terms_accepted_at, estimate?.terms_version, estimate?.terms_source]);

    const handleGenerateTermsLink = async () => {
        if (!estimate?.id) return;
        setTermsLoading(true);
        try {
            const result = await db.generateTermsLink(estimate.id);
            setTermsLink(publicLink('terms', result.token));
            setTermsSentAt(result.sentAt || new Date().toISOString());
            setTermsVersion(result.version || null);
            if (result.alreadyAccepted) {
                setTermsAcceptedAt(result.acceptedAt);
                showToast('Terms already signed — link reused', 'error');
            } else {
                showToast(result.alreadySent ? 'Existing terms link loaded' : 'Terms link generated');
            }
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to generate terms link'), 'error');
        } finally {
            setTermsLoading(false);
        }
    };

    const handleCopyTermsLink = async () => {
        if (!termsLink) return;
        try {
            await navigator.clipboard.writeText(termsLink);
            setTermsCopied(true);
            setTimeout(() => setTermsCopied(false), 2000);
        } catch {
            showToast('Copy failed — please select manually', 'error');
        }
    };

    const handleClearTermsLink = async () => {
        if (!estimate?.id) return;
        if (termsSource === 'carried_forward') {
            if (!(await confirmDialog({ title: `Clear ${confirmClientName}'s carried-forward signature?`, body: `This signature came from a previous estimate for ${confirmClientName}. Clearing it forces a fresh sign on this estimate only — the signature on the client record is untouched.`, confirmLabel: 'Clear signature', danger: true }))) return;
        } else if (termsAcceptedAt) {
            if (!(await confirmDialog({ title: `Clear ${confirmClientName}'s signed terms?`, body: `${confirmClientName} has already signed these terms. Clearing wipes the signature and its audit trail for this estimate and they will need to sign again. This can't be undone.`, confirmLabel: 'Clear signed terms', danger: true }))) return;
        } else {
            if (!(await confirmDialog({ title: `Clear ${confirmClientName}'s terms link?`, body: `The terms link already sent to ${confirmClientName} stops working. The estimate is kept — you can generate a new link afterwards.`, confirmLabel: 'Clear terms link', danger: true }))) return;
        }
        setTermsLoading(true);
        try {
            await db.clearTermsLink(estimate.id);
            setTermsLink(null);
            setTermsSentAt(null);
            setTermsAcceptedAt(null);
            setTermsVersion(null);
            setTermsSource(null);
            showToast('Terms link cleared');
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to clear'), 'error');
        } finally {
            setTermsLoading(false);
        }
    };

    // ===== WAIVER LIBRARY STATE =====
    // Many-per-move. Each waiver is its own row with its own token and audit
    // trail. See sql/018_waivers.sql + api/_lib/waivers-v1.js.
    const waiverTypeDefs = useMemo(() => db.listWaiverTypes(), []);
    const [waivers, setWaivers] = useState([]);
    const [waiversLoading, setWaiversLoading] = useState(false);
    const [addWaiverType, setAddWaiverType] = useState('');
    const [addWaiverNotes, setAddWaiverNotes] = useState('');
    const [addWaiverBusy, setAddWaiverBusy] = useState(false);
    const [waiverCopiedId, setWaiverCopiedId] = useState(null);

    // Load waivers on open / estimate change
    useEffect(() => {
        let cancelled = false;
        if (!estimate?.id) { setWaivers([]); return; }
        setWaiversLoading(true);
        db.listWaiversForEstimate(estimate.id)
            .then(rows => { if (!cancelled) setWaivers(rows || []); })
            .catch(e => { if (!cancelled) showToast('Error loading waivers: ' + e.message, 'error'); })
            .finally(() => { if (!cancelled) setWaiversLoading(false); });
        return () => { cancelled = true; };
    }, [estimate?.id]);

    const waiverUrl = (w) => publicLink('waiver', w.token);

    // Types that already exist as a pending (unsigned) waiver on this estimate.
    // We disable those in the dropdown to enforce the unique-pending rule
    // that also lives in the DB (idx_waivers_unique_pending).
    const pendingTypeKeys = useMemo(
        () => new Set(waivers.filter(w => !w.signed_at).map(w => w.waiver_type)),
        [waivers]
    );

    const handleAddWaiver = async () => {
        if (!estimate?.id || !addWaiverType) return;
        setAddWaiverBusy(true);
        try {
            const { waiver, reused } = await db.addWaiver(
                estimate.id,
                estimate.client_id,
                addWaiverType,
                { notes: addWaiverNotes.trim() || null }
            );
            setWaivers(prev => {
                const idx = prev.findIndex(w => w.id === waiver.id);
                if (idx === -1) return [...prev, waiver];
                const next = prev.slice();
                next[idx] = waiver;
                return next;
            });
            setAddWaiverType('');
            setAddWaiverNotes('');
            showToast(reused ? 'Existing pending waiver reused' : 'Waiver added');
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to add waiver'), 'error');
        } finally {
            setAddWaiverBusy(false);
        }
    };

    const handleCopyWaiverLink = async (w) => {
        try {
            await navigator.clipboard.writeText(waiverUrl(w));
            setWaiverCopiedId(w.id);
            setTimeout(() => setWaiverCopiedId(cur => cur === w.id ? null : cur), 2000);
        } catch {
            showToast('Copy failed — please select manually', 'error');
        }
    };

    const handleClearWaiver = async (w) => {
        if (w.signed_at) {
            showToast('Signed waivers cannot be cleared', 'error');
            return;
        }
        const waiverLabel = waiverTypeDefs.find(t => t.key === w.waiver_type)?.shortLabel || w.waiver_type;
        if (!(await confirmDialog({ title: `Remove the ${waiverLabel} waiver for ${confirmClientName}?`, body: `The unsigned ${waiverLabel} waiver link sent to ${confirmClientName} stops working and the waiver comes off this estimate. You can add it again later.`, confirmLabel: 'Remove waiver', danger: true }))) return;
        try {
            await db.clearWaiver(w.id);
            setWaivers(prev => prev.filter(x => x.id !== w.id));
            showToast('Waiver cleared');
        } catch (e) {
            showToast('Error: ' + (e.message || 'Failed to clear'), 'error');
        }
    };

    // ===== EDIT STATE (mirrors ClientPricingTab) =====
    const [editJobType, setEditJobType] = useState(estimate?.job_type === 'LONG_DISTANCE' ? 'LONG_DISTANCE' : 'LOCAL');
    const [editCrewSize, setEditCrewSize] = useState(estimate?.crew_size_needed || 0);
    const [editTruckType, setEditTruckType] = useState(estimate?.trucks_needed || '26ft');
    const [editExtraTrucks, setEditExtraTrucks] = useState(() => {
        try { return JSON.parse(estimate?.extra_trucks || '[]'); } catch { return []; }
    });
    // Waive the per-hour extra-truck surcharge for this estimate. The
    // extra trucks themselves stay required (dispatcher still allocates)
    // — only the pricing surcharge is suppressed. Hydrated from
    // estimates.extra_truck_fee_waived (sql/038).
    const [editExtraTruckFeeWaived, setEditExtraTruckFeeWaived] = useState(
        !!estimate?.extra_truck_fee_waived,
    );
    const [editHourlyRate, setEditHourlyRate] = useState(estimate?.hourly_rate || 0);
    const [editMinHours, setEditMinHours] = useState(estimate?.min_hours || '');
    const [editMaxHours, setEditMaxHours] = useState(estimate?.max_hours || '');
    const [editTravelFee, setEditTravelFee] = useState(estimate?.travel_fee || 0);
    const [editMaterialsFee, setEditMaterialsFee] = useState(estimate?.materials_fee || 0);
    const [editHoldFee, setEditHoldFee] = useState(estimate?.hold_fee || 0); // sql/088, 2026-09-11
    const [editPackingNeeded, setEditPackingNeeded] = useState((estimate?.packing_crew_size || 0) > 0);
    const [editPackingCrewSize, setEditPackingCrewSize] = useState(estimate?.packing_crew_size || 2);
    const [editPackingRate, setEditPackingRate] = useState(estimate?.packing_rate || 0);
    const [editPackingMinHours, setEditPackingMinHours] = useState(estimate?.packing_min_hours || estimate?.packing_hours || '');
    const [editPackingMaxHours, setEditPackingMaxHours] = useState(estimate?.packing_max_hours || estimate?.packing_hours || '');
    const [editHeavyItemCount, setEditHeavyItemCount] = useState(estimate?.heavy_item_count || 0);
    const [editRemovalFee, setEditRemovalFee] = useState(estimate?.removal_fee || 0);
    const [editBolPrice, setEditBolPrice] = useState(estimate?.bol_price || '');
    const [editBolReference, setEditBolReference] = useState(estimate?.bol_reference || '');
    const [editEstimatedWeight, setEditEstimatedWeight] = useState(estimate?.estimated_weight_lbs || '');

    const extraTruckFeePerHr = useMemo(() => {
        try { return parseFloat(JSON.parse(config?.extra_truck_fee || '25')); } catch { return 25; }
    }, [config]);
    const heavyItemFee = useMemo(() => {
        try { return parseFloat(JSON.parse(config?.heavy_item_fee || '200')); } catch { return 200; }
    }, [config]);

    // Auto-fill rates when crew/truck ACTUALLY CHANGES during editing —
    // never on edit-mode entry. The old version fired on hydration too, so
    // opening Edit Pricing replaced a stored rate OVERRIDE (negotiated
    // rates are normal on written estimates) with the rate-table default,
    // and a no-op Save silently repriced the estimate — it changed a
    // booked, client-visible quote by $210 in QA (Module-2 re-verify
    // NEW-5). First effect run after entering edit mode = hydration; skip.
    const editRateHydratedRef = useRef(false);
    const editPackingRateHydratedRef = useRef(false);
    useEffect(() => {
        if (!editMode) { editRateHydratedRef.current = false; editPackingRateHydratedRef.current = false; }
    }, [editMode]);
    useEffect(() => {
        if (!editMode || editJobType !== 'LOCAL') return;
        if (!editRateHydratedRef.current) { editRateHydratedRef.current = true; return; }
        const rate = lookupRate(config, editCrewSize, editTruckType, 'moving');
        if (rate > 0) setEditHourlyRate(rate);
        // config intentionally NOT in deps — a background config refresh
        // mid-edit must not overwrite the operator's rate either.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCrewSize, editTruckType, editJobType, editMode]);

    useEffect(() => {
        if (!editMode || !editPackingNeeded) return;
        if (!editPackingRateHydratedRef.current) { editPackingRateHydratedRef.current = true; return; }
        const rate = lookupPackingRate(config, editPackingCrewSize);
        if (rate > 0) setEditPackingRate(rate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editPackingNeeded, editPackingCrewSize, editMode]);

    // ===== EDIT TOTALS =====
    // Waiver collapses the surcharge to 0 — same semantics as the
    // create flow in ClientPricingTab.
    const editExtraSurchargePerHr = editExtraTruckFeeWaived
        ? 0
        : editExtraTrucks.length * extraTruckFeePerHr;
    const editEffectiveRate = (parseFloat(editHourlyRate) || 0) + editExtraSurchargePerHr;
    const editLaborMin = editJobType === 'LOCAL' ? (parseFloat(editMinHours) || 0) * editEffectiveRate : 0;
    const editLaborMax = editJobType === 'LOCAL' ? (parseFloat(editMaxHours) || parseFloat(editMinHours) || 0) * editEffectiveRate : 0;
    const editPackingMinTotal = editPackingNeeded ? (parseFloat(editPackingMinHours) || 0) * (parseFloat(editPackingRate) || 0) : 0;
    const editPackingMaxTotal = editPackingNeeded ? (parseFloat(editPackingMaxHours) || parseFloat(editPackingMinHours) || 0) * (parseFloat(editPackingRate) || 0) : 0;
    const editHeavyTotal = editHeavyItemCount * heavyItemFee;
    const editFixedAddOns = (parseFloat(editTravelFee) || 0) + (parseFloat(editHoldFee) || 0) + (parseFloat(editMaterialsFee) || 0) + editHeavyTotal + (parseFloat(editRemovalFee) || 0);
    const editTotalMin = editJobType === 'LOCAL' ? editLaborMin + editPackingMinTotal + editFixedAddOns : (parseFloat(editBolPrice) || 0) + editPackingMinTotal + editHeavyTotal + (parseFloat(editRemovalFee) || 0);
    const editTotalMax = editJobType === 'LOCAL' ? editLaborMax + editPackingMaxTotal + editFixedAddOns : (parseFloat(editBolPrice) || 0) + editPackingMaxTotal + editHeavyTotal + (parseFloat(editRemovalFee) || 0);
    const editIsRange = editTotalMin !== editTotalMax && editTotalMax > 0;

    if (!estimate?.id) { onClose(); return null; }

    const client = clients.find(c => c.id === estimate.client_id);
    const status = getEstimateStatus(estimate.status);
    const moveDate = estimate.move_date ? (() => { try { return format(parseISO(estimate.move_date), 'EEE, MMM d, yyyy'); } catch { return estimate.move_date; } })() : null;
    const isOOS = estimate.job_type === 'LONG_DISTANCE';

    // Status-change request handler — delegates to the shared hook, which
    // handles the warning dialog and commit. Kept as a thin wrapper so the
    // button onClicks below stay readable.
    const changeStatus = (newStatus) => statusChanger.request(newStatus);

    const savePricing = async () => {
        // Negative money/hours never valid (QA M2 BUG-6 — same guard as
        // ClientPricingTab / the BOL card; min="0" only gates spinners).
        const moneyFields = {
            'Hourly rate': editHourlyRate, 'Min hours': editMinHours, 'Max hours': editMaxHours,
            'Travel fee': editTravelFee, 'Hold fee': editHoldFee, 'Materials fee': editMaterialsFee, 'Removal fee': editRemovalFee,
            'Packing rate': editPackingRate, 'Packing min hours': editPackingMinHours, 'Packing max hours': editPackingMaxHours,
            'BOL price': editBolPrice, 'Estimated weight': editEstimatedWeight,
        };
        const negative = Object.entries(moneyFields).filter(([, v]) => (parseFloat(v) || 0) < 0);
        if (negative.length) {
            setPricingErrors(negative.map(([k]) => `${k} — can't be negative`));
            return;
        }
        setPricingErrors([]);
        setSaving(true);
        try {
            // job_type / packing_needed are the MOVE's (2026-09-18, rule 1):
            // the estimate's copies are a mirror the db strips on update, so
            // write them to the move first. Pricing packing in lifts the
            // move to 'yes'; pricing it out never downgrades the move.
            if (estMove) {
                const mvPatch = {};
                if (editJobType !== estMove.job_type) mvPatch.job_type = editJobType;
                if (editPackingNeeded && !['yes', 'partial', 'full'].includes(estMove.packing_needed || '')) mvPatch.packing_needed = 'yes';
                if (Object.keys(mvPatch).length) await saveMove({ id: estMove.id, ...mvPatch });
            }
            await onSave({
                ...estimate,
                job_type: editJobType,
                crew_size_needed: editCrewSize,
                trucks_needed: editTruckType,
                extra_trucks: JSON.stringify(editExtraTrucks),
                extra_truck_fee_waived: editJobType === 'LONG_DISTANCE' ? false : editExtraTruckFeeWaived,
                hourly_rate: editJobType === 'LOCAL' ? (parseFloat(editHourlyRate) || 0) : 0,
                estimated_hours: parseFloat(editMaxHours) || parseFloat(editMinHours) || 0,
                min_hours: parseFloat(editMinHours) || 0,
                max_hours: parseFloat(editMaxHours) || parseFloat(editMinHours) || 0,
                // LOCAL-only fees — never part of the OOS tariff formula
                // (QA M2 BUG-4; mirrors ClientPricingTab).
                travel_fee: editJobType === 'LOCAL' ? (parseFloat(editTravelFee) || 0) : 0,
                materials_fee: editJobType === 'LOCAL' ? (parseFloat(editMaterialsFee) || 0) : 0,
                hold_fee: editJobType === 'LOCAL' ? (parseFloat(editHoldFee) || 0) : 0,
                packing_needed: editPackingNeeded ? 'yes' : 'none',
                packing_crew_size: editPackingNeeded ? editPackingCrewSize : 0,
                packing_rate: editPackingNeeded ? (parseFloat(editPackingRate) || 0) : 0,
                packing_hours: editPackingNeeded ? (parseFloat(editPackingMaxHours) || parseFloat(editPackingMinHours) || 0) : 0,
                packing_min_hours: editPackingNeeded ? (parseFloat(editPackingMinHours) || 0) : 0,
                packing_max_hours: editPackingNeeded ? (parseFloat(editPackingMaxHours) || parseFloat(editPackingMinHours) || 0) : 0,
                heavy_item_count: editHeavyItemCount,
                heavy_item_fee: heavyItemFee,
                removal_fee: parseFloat(editRemovalFee) || 0,
                bol_price: editJobType === 'LONG_DISTANCE' ? (parseFloat(editBolPrice) || 0) : 0,
                bol_reference: editJobType === 'LONG_DISTANCE' ? editBolReference : '',
                estimated_weight_lbs: editJobType === 'LONG_DISTANCE' ? (parseFloat(editEstimatedWeight) || null) : null,
                total_estimate: editTotalMax,
                total_min: editTotalMin,
                total_max: editTotalMax,
            });
            showToast('Pricing updated');
            setEditMode(false);
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setSaving(false); }
    };

    const resetEditState = () => {
        setEditJobType(estimate?.job_type === 'LONG_DISTANCE' ? 'LONG_DISTANCE' : 'LOCAL');
        setEditCrewSize(estimate?.crew_size_needed || 0);
        setEditTruckType(estimate?.trucks_needed || '26ft');
        try { setEditExtraTrucks(JSON.parse(estimate?.extra_trucks || '[]')); } catch { setEditExtraTrucks([]); }
        setEditExtraTruckFeeWaived(!!estimate?.extra_truck_fee_waived);
        setEditHourlyRate(estimate?.hourly_rate || 0);
        setEditMinHours(estimate?.min_hours || '');
        setEditMaxHours(estimate?.max_hours || '');
        setEditTravelFee(estimate?.travel_fee || 0);
        setEditMaterialsFee(estimate?.materials_fee || 0);
        setEditHoldFee(estimate?.hold_fee || 0);
        setEditPackingNeeded((estimate?.packing_crew_size || 0) > 0);
        setEditPackingCrewSize(estimate?.packing_crew_size || 2);
        setEditPackingRate(estimate?.packing_rate || 0);
        setEditPackingMinHours(estimate?.packing_min_hours || estimate?.packing_hours || '');
        setEditPackingMaxHours(estimate?.packing_max_hours || estimate?.packing_hours || '');
        setEditHeavyItemCount(estimate?.heavy_item_count || 0);
        setEditRemovalFee(estimate?.removal_fee || 0);
        setEditBolPrice(estimate?.bol_price || '');
        setEditBolReference(estimate?.bol_reference || '');
        setEditEstimatedWeight(estimate?.estimated_weight_lbs || '');
    };

    const openClient = () => {
        if (client?.id) { onClose(); navigateToClient(client.id); }
    };

    // (The "Revise (new version)" button that lived here was replaced
    // 2026-06-09 by the deposit-transfer flow: see the Transfer control
    // next to the deposit pill on EstimatesPage and the transfer option
    // in ClientPricingTab's same-move dialog. db.transferDepositTrail
    // is the engine for both.)

    // Calculate display values
    const hasRange = estimate.min_hours && estimate.max_hours && estimate.min_hours !== estimate.max_hours;
    // Multi-day local pricing (2026-06-09): priced day entries on
    // planned_phases. Presence of any flips the labor display from the
    // flat hours×rate line to per-day lines.
    // 2026-09-10: read through phaseLaborPricing so a pack+move COMBO
    // day's packing hours (pack_* slots) never count as a priced labor
    // day — that was Alwin's "Day 1 Labor 3 hrs × $125" (BBM-2609-018).
    const pricedDayPhases = Array.isArray(estimate.planned_phases)
        ? estimate.planned_phases.filter(p => p && p.kind !== 'hold' && phaseHasLaborPricing(p))
        : [];
    const packingMinHrs = estimate.packing_min_hours || estimate.packing_hours || 0;
    const packingMaxHrs = estimate.packing_max_hours || estimate.packing_hours || 0;
    const packingMinDisplay = packingMinHrs * (estimate.packing_rate || 0);
    const packingMaxDisplay = packingMaxHrs * (estimate.packing_rate || 0);
    const hasPackingRange = packingMinHrs && packingMaxHrs && packingMinHrs !== packingMaxHrs;
    const heavyTotal = (estimate.heavy_item_count || 0) * (estimate.heavy_item_fee || 0);

    // Portal to <body> so the modal escapes any transformed ancestor.
    // EstimateModal is opened from inside ClientIntakeModal, which uses
    // the new <Modal> wrapper (already portaled) but still applies
    // `transform: scale(1)` to its content for the open animation. Any
    // `position: fixed` rendered inside that transformed subtree becomes
    // fixed-to-the-modal instead of fixed-to-the-viewport, so this modal
    // would render at the top of the parent modal's scroll area.
    return createPortal(
        // role="dialog" + aria-modal (QA M2 re-verify NEW-6): this editor is
        // hand-rolled (predates <Modal>), so without dialog semantics the
        // App.jsx shortcut guard couldn't see it — one stray body-focus
        // keystroke navigated away and destroyed the pricing draft. Also
        // what screen readers key modal behavior off.
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="modal-content w-full max-w-2xl" onClick={e => e.stopPropagation()}>
                {confirmElement}
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-bold text-slate-900">{estimate.job_number}</h2>
                        <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold" style={{ background: statusTint(status.color, status.bg), color: status.color }}>{status.label}</span>
                        {isOOS && <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-blue-100 text-blue-700">OOS / BOL</span>}
                    </div>
                    <IconButton label="Close estimate" onClick={onClose}><IconX size={18}/></IconButton>
                </div>

                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-lg">
                            {(client?.name || '?')[0]}
                        </div>
                        <div>
                            <div className="font-bold text-slate-900">{client?.name || 'Unknown'}</div>
                            <div className="text-xs text-slate-500">{formatPhone(client?.phone)} {client?.email ? `· ${client.email}` : ''}</div>
                            {client?.id && (
                                <Button variant="tertiary" size="sm" className="mt-0.5" onClick={openClient} rightIcon={<IconArrowRight size={10}/>}>
                                    Open Client File
                                </Button>
                            )}
                        </div>
                        {canViewPricing && (
                            <div className="ml-auto text-right">
                                <div className="text-2xl font-extrabold text-green-600">
                                    {estimate.total_min && estimate.total_min !== estimate.total_max
                                        ? `$${Number(estimate.total_min).toLocaleString()} – $${Number(estimate.total_max).toLocaleString()}`
                                        : `$${Number(estimate.total_estimate || 0).toLocaleString()}`
                                    }
                                </div>
                                {isOOS && <span className="text-[10px] text-blue-500 font-medium">Bill of Lading</span>}
                            </div>
                        )}
                    </div>

                    {/* Assignment row — admin+ can pick / clear the assignee.
                        For office staff this renders as a display-only label
                        (they only see estimates assigned to them anyway, so
                        this just confirms "yes, this is yours"). */}
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">Assigned To</label>
                        <div className="max-w-xs">
                            <AssigneePicker estimate={estimate} variant="modal" />
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1.5">
                            Only the assignee + admins can view this estimate. Unassigned estimates are admin-only.
                        </p>
                    </div>

                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">Update Status</label>
                        <div className="flex gap-2 flex-wrap">
                            {ESTIMATE_STATUSES.map(s => {
                                const isCurrent = estimate.status === s.key;
                                const allowed = isTransitionAllowed(ESTIMATE_STATUSES, estimate.status, s.key);
                                // Visual treatment per state:
                                //   • current: ring + scale (unchanged)
                                //   • allowed forward step: normal hover (opacity-100 on hover)
                                //   • disallowed/unusual transition: faded but still clickable
                                //     (clicking surfaces the warning dialog rather than blocking).
                                const cls = isCurrent
                                    ? 'ring-2 ring-offset-1 scale-105'
                                    : allowed
                                        ? 'opacity-60 hover:opacity-100'
                                        : 'opacity-30 hover:opacity-70';
                                return (
                                    <button key={s.key} onClick={() => changeStatus(s.key)} disabled={saving}
                                        title={!isCurrent && !allowed ? 'Unusual transition — will prompt for confirmation' : undefined}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${cls}`}
                                        style={{ background: statusTint(s.color, s.bg), color: s.color }}>
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ===== WRITTEN ESTIMATE (client-facing ballpark link) ===== */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2 min-w-0">
                                <IconFileText size={16} className="text-amber-700 flex-shrink-0 mt-0.5"/>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-amber-900">Written Estimate (Client-Facing)</div>
                                    <div className="text-[11px] text-amber-700 mt-0.5">Non-binding ballpark — hosted link client can open &amp; print as PDF</div>
                                </div>
                            </div>
                            {!writtenLink ? (
                                <Button size="sm" className="flex-shrink-0" onClick={() => setWrittenModal(true)}>
                                    Send Written Estimate
                                </Button>
                            ) : (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-amber-200 text-amber-900 rounded-lg text-[10px] font-bold tracking-wide">SENT{writtenLinkSentAt ? ` · ${format(parseISO(writtenLinkSentAt), 'MMM d')}` : ''}</span>
                            )}
                        </div>

                        {writtenLink && (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2">
                                    <input
                                        readOnly
                                        value={writtenLink}
                                        onFocus={e => e.target.select()}
                                        className="flex-1 bg-transparent text-xs text-slate-700 outline-none font-mono truncate"
                                    />
                                    <Button variant="secondary" size="sm" className="flex-shrink-0" onClick={handleCopyWrittenLink}>
                                        {writtenCopied ? <><IconCheck size={12}/> Copied</> : <><IconClipboard size={12}/> Copy</>}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between text-[11px]">
                                    <a
                                        href={writtenLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-amber-700 hover:text-amber-900 font-semibold"
                                    >Preview hosted view →</a>
                                    <span className="text-amber-600">
                                        {estimate.written_estimate_viewed_at
                                            ? `Opened ${format(parseISO(estimate.written_estimate_viewed_at), 'MMM d, h:mm a')}`
                                            : 'Not yet opened by client'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ===== DEPOSIT LINK (Square) ===== */}
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2 min-w-0">
                                <IconDollarSign size={16} className="text-red-700 flex-shrink-0 mt-0.5"/>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-red-900">Deposit Link (Square)</div>
                                    <div className="text-[11px] text-red-700 mt-0.5">
                                        {depositPaidAt
                                            ? `Paid ${format(parseISO(depositPaidAt), 'MMM d, h:mm a')}${depositCardBrand && depositLast4 ? ` · ${depositCardBrand} ••${depositLast4}` : ''}`
                                            : depositLink
                                                ? `Awaiting payment${depositSentAt ? ` · link generated ${format(parseISO(depositSentAt), 'MMM d')}` : ''}`
                                                : 'Reserve the move date with a Square card deposit'}
                                    </div>
                                </div>
                            </div>
                            {depositPaidAt ? (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-green-200 text-green-900 rounded-lg text-[10px] font-bold tracking-wide">PAID ${Number(estimate.deposit_amount || 0).toLocaleString()}</span>
                            ) : depositLink ? (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-red-200 text-red-900 rounded-lg text-[10px] font-bold tracking-wide">SENT ${Number(depositLinkAmount || 0).toLocaleString()}</span>
                            ) : null}
                        </div>

                        {/* Amount picker (only if no link + not paid) */}
                        {!depositLink && !depositPaidAt && (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {depositPresets.map(p => (
                                        <Button
                                            key={p}
                                            variant="secondary"
                                            size="sm"
                                            disabled={depositLoading}
                                            onClick={() => handleGenerateDepositLink(p)}
                                        >
                                            ${p}
                                        </Button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <NumberField
                                        value={depositAmountInput}
                                        onChange={v => setDepositAmountInput(v)}
                                        step={5}
                                        prefix="$"
                                        placeholder="Custom amount"
                                        ariaLabel="Custom deposit amount"
                                        className="flex-1"
                                    />
                                    <Button
                                        size="sm"
                                        className="flex-shrink-0"
                                        loading={depositLoading}
                                        disabled={!(Number(depositAmountInput) > 0)}
                                        onClick={() => handleGenerateDepositLink(depositAmountInput)}
                                    >
                                        Generate Link
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Link + copy (if generated, paid or unpaid) */}
                        {depositLink && (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 bg-white border border-red-200 rounded-lg px-3 py-2">
                                    <input
                                        readOnly
                                        value={depositLink}
                                        onFocus={e => e.target.select()}
                                        className="flex-1 bg-transparent text-xs text-slate-700 outline-none font-mono truncate"
                                    />
                                    <Button variant="secondary" size="sm" className="flex-shrink-0" onClick={handleCopyDepositLink}>
                                        {depositCopied ? <><IconCheck size={12}/> Copied</> : <><IconClipboard size={12}/> Copy</>}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between text-[11px]">
                                    <a
                                        href={depositLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-red-700 hover:text-red-900 font-semibold"
                                    >Preview deposit page →</a>
                                    {depositPaidAt && depositReceiptUrl && (
                                        <a
                                            href={depositReceiptUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-green-700 hover:text-green-900 font-semibold"
                                        >View receipt →</a>
                                    )}
                                    {!depositPaidAt && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={handleClearDepositLink}
                                            disabled={depositLoading}
                                        >Clear link</Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ===== TERMS OF SERVICE LINK ===== */}
                    {/* termsSource === 'carried_forward' means the client already signed
                        the current version on a previous estimate within the last year and
                        this estimate inherited that signature automatically on create. We
                        show a distinct "Carried forward" state and hide Generate Link. */}
                    <div className={`${termsSource === 'carried_forward' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} border rounded-xl p-4`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2 min-w-0">
                                <IconFileText size={16} className={`${termsSource === 'carried_forward' ? 'text-emerald-700' : 'text-red-700'} flex-shrink-0 mt-0.5`}/>
                                <div className="min-w-0">
                                    <div className={`text-sm font-bold ${termsSource === 'carried_forward' ? 'text-emerald-900' : 'text-red-900'}`}>Terms of Service</div>
                                    <div className={`text-[11px] mt-0.5 ${termsSource === 'carried_forward' ? 'text-emerald-700' : 'text-red-700'}`}>
                                        {termsSource === 'carried_forward' && termsAcceptedAt
                                            ? `Carried forward · client signed ${format(parseISO(termsAcceptedAt), 'MMM d, yyyy')}${termsVersion ? ` · ${termsVersion}` : ''}`
                                            : termsAcceptedAt
                                                ? `Signed ${format(parseISO(termsAcceptedAt), 'MMM d, h:mm a')}${termsVersion ? ` · ${termsVersion}` : ''}`
                                                : termsLink
                                                    ? `Awaiting signature${termsSentAt ? ` · link generated ${format(parseISO(termsSentAt), 'MMM d')}` : ''}`
                                                    : 'Send a lightweight clickwrap Terms of Service link'}
                                    </div>
                                </div>
                            </div>
                            {termsSource === 'carried_forward' ? (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-emerald-200 text-emerald-900 rounded-lg text-[10px] font-bold tracking-wide">CARRIED FORWARD</span>
                            ) : termsAcceptedAt ? (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-green-200 text-green-900 rounded-lg text-[10px] font-bold tracking-wide">SIGNED</span>
                            ) : termsLink ? (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-red-200 text-red-900 rounded-lg text-[10px] font-bold tracking-wide">SENT</span>
                            ) : null}
                        </div>

                        {/* Carry-forward explainer — shown only when a client-level signature
                            was auto-applied to this estimate. No Generate button because a
                            fresh link would be disallowed by the API until the operator clears
                            the inherited signature. */}
                        {termsSource === 'carried_forward' && (
                            <div className="mt-3 text-[11px] leading-relaxed text-emerald-800 bg-white/60 border border-emerald-200 rounded-lg px-3 py-2">
                                This client's electronic signature is valid for up to one year
                                from the date they first signed, as long as the Terms have not
                                changed. No action needed — the estimate is already covered.
                            </div>
                        )}

                        {/* Generate button (only if no link and no carry-forward signature) */}
                        {!termsLink && termsSource !== 'carried_forward' && (
                            <div className="mt-3">
                                <Button
                                    size="sm"
                                    full
                                    loading={termsLoading}
                                    onClick={handleGenerateTermsLink}
                                >
                                    Generate Terms Link
                                </Button>
                            </div>
                        )}

                        {/* Clear-signature escape hatch for carry-forward state.
                            Allows an operator to force a fresh sign on this specific
                            estimate without touching the client-level signature. */}
                        {termsSource === 'carried_forward' && !termsLink && (
                            <div className="mt-3 flex items-center justify-end text-[11px]">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={handleClearTermsLink}
                                    disabled={termsLoading}
                                >Require fresh signature on this estimate</Button>
                            </div>
                        )}

                        {/* Link + copy */}
                        {termsLink && (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 bg-white border border-red-200 rounded-lg px-3 py-2">
                                    <input
                                        readOnly
                                        value={termsLink}
                                        onFocus={e => e.target.select()}
                                        className="flex-1 bg-transparent text-xs text-slate-700 outline-none font-mono truncate"
                                    />
                                    <Button variant="secondary" size="sm" className="flex-shrink-0" onClick={handleCopyTermsLink}>
                                        {termsCopied ? <><IconCheck size={12}/> Copied</> : <><IconClipboard size={12}/> Copy</>}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between text-[11px]">
                                    <a
                                        href={termsLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-red-700 hover:text-red-900 font-semibold"
                                    >Preview terms page →</a>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={handleClearTermsLink}
                                        disabled={termsLoading}
                                    >{termsAcceptedAt ? 'Clear signature' : 'Clear link'}</Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ===== WAIVER LIBRARY ===== */}
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2 min-w-0">
                                <IconFileText size={16} className="text-orange-700 flex-shrink-0 mt-0.5"/>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-orange-900">Waivers</div>
                                    <div className="text-[11px] text-orange-700 mt-0.5">
                                        {waiversLoading
                                            ? <Skeleton className="h-2.5 w-44" />
                                            : waivers.length === 0
                                                ? 'Add situational waivers (appliance, oversized, thin TV, pre-damaged, wrap decline)'
                                                : `${waivers.length} waiver${waivers.length === 1 ? '' : 's'} on this move · ${waivers.filter(w => w.signed_at).length} signed`}
                                    </div>
                                </div>
                            </div>
                            {waivers.length > 0 && waivers.every(w => w.signed_at) && (
                                <span className="flex-shrink-0 px-2.5 py-1 bg-green-200 text-green-900 rounded-lg text-[10px] font-bold tracking-wide">ALL SIGNED</span>
                            )}
                        </div>

                        {/* Existing waivers list */}
                        {waiversLoading && waivers.length === 0 && <RowSkeleton rows={3} height="h-10" className="mt-3" />}
                        {waivers.length > 0 && (
                            <div className="mt-3 space-y-2">
                                {waivers.map(w => {
                                    const def = waiverTypeDefs.find(t => t.key === w.waiver_type);
                                    const label = def?.shortLabel || w.waiver_type;
                                    const signed = !!w.signed_at;
                                    return (
                                        <div key={w.id} className="bg-white border border-orange-200 rounded-lg p-3">
                                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                                <div className="min-w-0">
                                                    <div className="text-xs font-bold text-slate-900">{label}</div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                                        {signed
                                                            ? `Signed by ${w.signed_name || 'client'} · ${format(parseISO(w.signed_at), 'MMM d, h:mm a')}${w.waiver_version ? ` · ${w.waiver_version}` : ''}`
                                                            : `Awaiting signature${w.sent_at ? ` · created ${format(parseISO(w.sent_at), 'MMM d')}` : ''}`
                                                        }
                                                    </div>
                                                    {w.notes && (
                                                        <div className="text-[10px] text-slate-600 mt-1 italic">Note: {w.notes}</div>
                                                    )}
                                                </div>
                                                <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[9px] font-bold tracking-wide ${signed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                                    {signed ? 'SIGNED' : 'SENT'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 text-[11px]">
                                                <div className="flex items-center gap-2">
                                                    <Button variant="secondary" size="sm" onClick={() => handleCopyWaiverLink(w)}>
                                                        {waiverCopiedId === w.id ? <><IconCheck size={11}/> Copied</> : <><IconClipboard size={11}/> Copy link</>}
                                                    </Button>
                                                    <a
                                                        href={waiverUrl(w)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-orange-700 hover:text-orange-900 font-semibold"
                                                    >Preview →</a>
                                                </div>
                                                {!signed && (
                                                    <Button variant="danger" size="sm" onClick={() => handleClearWaiver(w)}>Clear</Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Add waiver form */}
                        <div className="mt-3 pt-3 border-t border-orange-200 space-y-2">
                            <label className="text-[10px] font-bold text-orange-900 uppercase tracking-wider block">Add Waiver</label>
                            <ChoiceKeys
                                ariaLabel="Waiver type"
                                size="sm"
                                items={waiverTypeDefs.map(def => {
                                    const disabled = pendingTypeKeys.has(def.key);
                                    return {
                                        value: def.key,
                                        label: `${def.shortLabel}${disabled ? ' (already pending)' : ''}`,
                                        disabled,
                                        title: disabled ? 'A pending waiver of this type already exists' : undefined,
                                    };
                                })}
                                value={addWaiverType}
                                onChange={v => setAddWaiverType(v)}
                            />
                            {addWaiverType && (
                                <>
                                    <div className="text-[10px] text-orange-800 italic">
                                        {waiverTypeDefs.find(t => t.key === addWaiverType)?.summary}
                                    </div>
                                    <textarea
                                        value={addWaiverNotes}
                                        onChange={e => setAddWaiverNotes(e.target.value)}
                                        placeholder="Optional context (e.g. 'Sub-Zero fridge + ice maker', '2nd-floor upright piano')"
                                        rows={2}
                                        className="w-full border border-orange-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 placeholder:text-slate-400"
                                    />
                                </>
                            )}
                            <Button
                                size="sm"
                                full
                                disabled={!addWaiverType}
                                loading={addWaiverBusy}
                                onClick={handleAddWaiver}
                            >
                                + Add Waiver
                            </Button>
                        </div>
                    </div>

                    {(estimate.origin_address || estimate.destination_address) && (
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase">Route</span>
                                {client?.id && <Button variant="tertiary" size="sm" onClick={openClient}>Edit in Client File</Button>}
                            </div>
                            {estimate.origin_address && <div className="text-sm text-slate-700"><span className="text-red-500 font-bold">From:</span> {estimate.origin_address}</div>}
                            {estimate.destination_address && <div className="text-sm text-slate-700 mt-1"><span className="text-blue-500 font-bold">To:</span> {estimate.destination_address}</div>}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        {/* Read-only (2026-09-18, rule 1): dates are edited on the
                            move — the Estimates page header strip or the client
                            file. Reads the move for the tentative marker; falls
                            back to the mirror column for an unlinked row. */}
                        {(estMove?.load_date || moveDate) && (
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{estMove?.unload_date ? 'Load' : 'Move Date'}</span>
                                <span className={`text-sm font-medium ${estMove?.load_tentative ? 'text-amber-700' : 'text-slate-700'}`}>
                                    {estMove ? fmtMoveDate(estMove.load_date, { tentative: estMove.load_tentative, year: true }) : moveDate}
                                    {estMove?.unload_date && <span className="text-slate-500"> · Unload <span className={estMove.unload_tentative ? 'text-amber-700' : ''}>{fmtMoveDate(estMove.unload_date, { tentative: estMove.unload_tentative, windowEnd: estMove.unload_window_end, year: true })}</span></span>}
                                </span>
                                <span className="block text-[10px] text-slate-400 mt-0.5">Dates live on the move — edit on the Estimates page header or the client file</span>
                            </div>
                        )}
                        <div><span className="text-[10px] font-bold text-slate-400 uppercase block">Job Type</span><span className="text-sm font-medium text-slate-700">{isOOS ? 'Out-of-State (BOL)' : estimate.job_type}</span></div>
                        {estimate.crew_size_needed > 0 && <div><span className="text-[10px] font-bold text-slate-400 uppercase block">Crew</span><span className="text-sm font-medium text-slate-700">{estimate.crew_size_needed} people</span></div>}
                        {estimateTruckLabel(estimate) && <div><span className="text-[10px] font-bold text-slate-400 uppercase block">{estimateTruckLabel(estimate).includes('+') ? 'Trucks' : 'Truck'}</span><span className="text-sm font-medium text-slate-700">{estimateTruckLabel(estimate)}</span></div>}
                    </div>

                    {/* ===== PRICING BREAKDOWN =====
                        Visible to anyone with view_estimate_pricing. The
                        Edit Pricing toggle inside is further gated on
                        edit_pricing — so view-only users see breakdown
                        but can't open the editor. */}
                    {canViewPricing && (
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconDollarSign size={13}/>Pricing Breakdown</h3>
                                {!editMode && canEditPricing && <Button variant="tertiary" size="sm" onClick={() => setEditMode(true)}>Edit Pricing</Button>}
                            </div>

                            {editMode ? (
                                <div className="space-y-4">
                                    <FormErrorBanner items={pricingErrors} />
                                    {/* Job Type Toggle */}
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditJobType('LOCAL')}
                                            className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${editJobType === 'LOCAL' ? 'bg-red-600 text-white border-red-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'}`}>
                                            Local / Hourly
                                        </button>
                                        <button onClick={() => setEditJobType('LONG_DISTANCE')}
                                            className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${editJobType === 'LONG_DISTANCE' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                                            Out-of-State (BOL)
                                        </button>
                                    </div>

                                    {/* LOCAL fields */}
                                    {editJobType === 'LOCAL' && (
                                        <>
                                            {/* Crew Size */}
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1.5">Crew Size</span>
                                                <div className="flex items-center gap-1.5">
                                                    {[2, 3, 4, 5, 6].map(n => (
                                                        <button key={n} type="button" onClick={() => setEditCrewSize(n)}
                                                            className={`w-9 h-9 rounded-lg text-xs font-bold border-2 transition-all ${editCrewSize === n ? 'bg-red-600 text-white border-red-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-red-300'}`}>
                                                            {n}
                                                        </button>
                                                    ))}
                                                    {editCrewSize > 0 && <span className="text-[10px] text-slate-400 ml-1">{editCrewSize} movers</span>}
                                                </div>
                                            </div>
                                            {/* Truck */}
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1.5">Primary Truck</span>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {TRUCK_TYPE_OPTIONS.map(t => (
                                                        <button key={t.value} type="button" onClick={() => setEditTruckType(t.value)}
                                                            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border-2 transition-all ${editTruckType === t.value ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'}`}>
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {editTruckType !== 'none' && (
                                                    <div className="mt-2">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-[10px] font-bold text-slate-400">Extra Trucks</span>
                                                            <span className="text-[10px] text-slate-400">(+${extraTruckFeePerHr}/hr each)</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <Button variant="tertiary" size="sm" onClick={() => setEditExtraTrucks(prev => [...prev, '16ft'])}>+ 16'</Button>
                                                            <Button variant="tertiary" size="sm" onClick={() => setEditExtraTrucks(prev => [...prev, '26ft'])}>+ 26'</Button>
                                                            {editExtraTrucks.map((t, i) => (
                                                                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-[11px] font-semibold">
                                                                    {t === '16ft' ? "16'" : "26'"}
                                                                    <button onClick={() => setEditExtraTrucks(prev => prev.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-500 font-bold">&times;</button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                        {editExtraTrucks.length > 0 && (
                                                            <>
                                                                <p className="text-[10px] mt-1">
                                                                    {editExtraTruckFeeWaived ? (
                                                                        <span className="text-amber-600 font-semibold">
                                                                            {editExtraTrucks.length} extra truck{editExtraTrucks.length > 1 ? 's' : ''} — surcharge waived
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-blue-500">
                                                                            {editExtraTrucks.length} extra truck{editExtraTrucks.length > 1 ? 's' : ''} = +${editExtraSurchargePerHr}/hr
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                <Switch
                                                                    size="sm"
                                                                    className="mt-1.5"
                                                                    checked={!!editExtraTruckFeeWaived}
                                                                    onChange={bool => setEditExtraTruckFeeWaived(bool)}
                                                                    label="Waive surcharge"
                                                                    description="Truck still required"
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Rate */}
                                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase">Hourly Rate</span>
                                                    {/* Honest labeling (NEW-5): "Auto-filled" only when the value
                                                        IS the table rate; a stored override shows as such. */}
                                                    {editHourlyRate > 0 && (
                                                        parseFloat(editHourlyRate) === lookupRate(config, editCrewSize, editTruckType, 'moving')
                                                            ? <span className="text-[10px] text-green-600">Auto-filled</span>
                                                            : <span className="text-[10px] text-amber-600">Custom rate</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <NumberField className="flex-1" inputClassName="font-bold" step={5} prefix="$" ariaLabel="Hourly rate" value={editHourlyRate || ''} onChange={v => setEditHourlyRate(v)} placeholder="0" />
                                                    <span className="text-xs text-slate-400">/hr</span>
                                                </div>
                                                {editExtraTrucks.length > 0 && (
                                                    editExtraTruckFeeWaived ? (
                                                        <div className="mt-1.5 text-[11px] text-amber-600 font-medium">
                                                            Surcharge waived — effective rate stays at <span className="font-bold">${editEffectiveRate}/hr</span>
                                                        </div>
                                                    ) : (
                                                        <div className="mt-1.5 text-[11px] text-blue-600 font-medium">
                                                            Effective: ${editHourlyRate}/hr + ${editExtraSurchargePerHr}/hr = <span className="font-bold">${editEffectiveRate}/hr</span>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            {/* Hours */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <FormField label="Min Hours">
                                                    <NumberField step={0.5} value={editMinHours} onChange={v => setEditMinHours(v)} placeholder="e.g. 4" />
                                                </FormField>
                                                <FormField label="Max Hours">
                                                    <NumberField step={0.5} value={editMaxHours} onChange={v => setEditMaxHours(v)} placeholder="e.g. 6" />
                                                </FormField>
                                            </div>
                                            {/* Travel & Materials */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <FormField label="Travel Fee ($)">
                                                    <NumberField step={5} prefix="$" value={editTravelFee || ''} onChange={v => setEditTravelFee(v)} placeholder="0" />
                                                </FormField>
                                                <FormField label="Materials Fee ($)">
                                                    <NumberField step={5} prefix="$" value={editMaterialsFee || ''} onChange={v => setEditMaterialsFee(v)} placeholder="0" />
                                                </FormField>
                                            </div>
                                            {/* Hold fee (sql/088) — flat charge for holding the
                                                loaded truck between days; mirrors ClientPricingTab. */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <FormField label="Hold Fee ($)">
                                                    <NumberField step={5} prefix="$" value={editHoldFee || ''} onChange={v => setEditHoldFee(v)} placeholder="0" />
                                                </FormField>
                                            </div>
                                        </>
                                    )}

                                    {/* OOS / BOL fields */}
                                    {editJobType === 'LONG_DISTANCE' && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-3">
                                            <FormField label="Estimated Shipment Weight (lbs)">
                                                <NumberField step={100} min={0} suffix="lbs" inputClassName="font-bold" value={editEstimatedWeight} onChange={v => setEditEstimatedWeight(v)} placeholder="e.g. 6500" />
                                            </FormField>
                                            <FormField label="BOL Move Price (flat rate)">
                                                <NumberField step={50} prefix="$" inputClassName="font-bold" value={editBolPrice} onChange={v => setEditBolPrice(v)} placeholder="Total from tariff" />
                                            </FormField>
                                            <FormField label="BOL / Reference #">
                                                <input className={inputCls + ' border-blue-200'} value={editBolReference} onChange={e => setEditBolReference(e.target.value)} placeholder="BOL number" />
                                            </FormField>
                                        </div>
                                    )}

                                    {/* Packing */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase">Packing Services</span>
                                            <button onClick={() => setEditPackingNeeded(!editPackingNeeded)}
                                                className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-all ${editPackingNeeded ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400 hover:text-slate-600'}`}>
                                                {editPackingNeeded ? 'Packing Added' : '+ Add Packing'}
                                            </button>
                                        </div>
                                        {editPackingNeeded && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <FormField label="Crew">
                                                        <ChoiceKeys
                                                            size="sm"
                                                            items={[1, 2, 3, 4].map(n => ({ value: n, label: `${n} ${n === 1 ? 'packer' : 'packers'}` }))}
                                                            value={editPackingCrewSize}
                                                            onChange={v => setEditPackingCrewSize(parseInt(v))}
                                                        />
                                                    </FormField>
                                                    <FormField label="Rate ($/hr)">
                                                        <NumberField size="sm" step={5} prefix="$" inputClassName="font-semibold" value={editPackingRate || ''} onChange={v => setEditPackingRate(v)} placeholder="0" />
                                                    </FormField>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <FormField label="Min Hours">
                                                        <NumberField size="sm" step={0.5} value={editPackingMinHours} onChange={v => setEditPackingMinHours(v)} placeholder="e.g. 4" />
                                                    </FormField>
                                                    <FormField label="Max Hours">
                                                        <NumberField size="sm" step={0.5} value={editPackingMaxHours} onChange={v => setEditPackingMaxHours(v)} placeholder="e.g. 8" />
                                                    </FormField>
                                                </div>
                                                {editPackingMinTotal > 0 && (
                                                    <div className="text-[11px] text-amber-700 font-semibold text-right mt-1.5">
                                                        Packing: {editPackingMinTotal !== editPackingMaxTotal && editPackingMaxTotal > 0
                                                            ? `$${editPackingMinTotal.toLocaleString()} – $${editPackingMaxTotal.toLocaleString()}`
                                                            : `$${editPackingMinTotal.toLocaleString()}`
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Heavy Items & Removal */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-[10px] font-bold text-rose-700">Heavy Items</span>
                                                <span className="text-[9px] text-rose-400">${heavyItemFee} each</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <NumberField
                                                    size="sm"
                                                    step={1}
                                                    className="flex-1"
                                                    inputClassName="font-bold text-rose-800"
                                                    ariaLabel="Heavy item count"
                                                    value={editHeavyItemCount}
                                                    onChange={v => setEditHeavyItemCount(Math.max(0, parseInt(v) || 0))}
                                                />
                                                {editHeavyTotal > 0 && <span className="text-[10px] font-semibold text-rose-600 ml-1">${editHeavyTotal}</span>}
                                            </div>
                                        </div>
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                                            <span className="text-[10px] font-bold text-slate-600 block mb-1.5">Removal Fee</span>
                                            <NumberField size="sm" step={5} prefix="$" ariaLabel="Removal fee" value={editRemovalFee || ''} onChange={v => setEditRemovalFee(v)} placeholder="0" />
                                        </div>
                                    </div>

                                    {/* Total */}
                                    {(editTotalMin > 0 || editTotalMax > 0) && (
                                        <div className="bg-slate-800 text-white rounded-xl p-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold uppercase tracking-wide">New Total</span>
                                                <span className="text-2xl font-extrabold">
                                                    {editIsRange
                                                        ? `$${editTotalMin.toLocaleString()} – $${editTotalMax.toLocaleString()}`
                                                        : `$${editTotalMin.toLocaleString()}`
                                                    }
                                                </span>
                                            </div>
                                            <div className="mt-2 space-y-0.5 text-[10px] text-slate-400">
                                                {editJobType === 'LOCAL' && (editMinHours || editMaxHours) && (
                                                    <div>Labor: {editMinHours}{editMaxHours && editMaxHours !== editMinHours ? `–${editMaxHours}` : ''} hrs × ${editEffectiveRate}/hr</div>
                                                )}
                                                {editJobType === 'LONG_DISTANCE' && editBolPrice && <div>BOL Move: ${parseFloat(editBolPrice).toLocaleString()}</div>}
                                                {editPackingMinTotal > 0 && <div>Packing: {editPackingMinHours}{editPackingMaxHours && editPackingMaxHours !== editPackingMinHours ? `–${editPackingMaxHours}` : ''} hrs × ${editPackingRate}/hr = ${editPackingMinTotal.toLocaleString()}{editPackingMinTotal !== editPackingMaxTotal && editPackingMaxTotal > 0 ? ` – $${editPackingMaxTotal.toLocaleString()}` : ''}</div>}
                                                {editTravelFee > 0 && <div>Travel: ${parseFloat(editTravelFee).toLocaleString()}</div>}
                                                {editHoldFee > 0 && <div>Hold: ${parseFloat(editHoldFee).toLocaleString()}</div>}
                                                {editMaterialsFee > 0 && <div>Materials: ${parseFloat(editMaterialsFee).toLocaleString()}</div>}
                                                {editHeavyTotal > 0 && <div>Heavy: {editHeavyItemCount} × ${heavyItemFee} = ${editHeavyTotal.toLocaleString()}</div>}
                                                {editRemovalFee > 0 && <div>Removal: ${parseFloat(editRemovalFee).toLocaleString()}</div>}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-2 justify-end">
                                        <Button variant="secondary" size="sm" onClick={() => { setEditMode(false); setPricingErrors([]); resetEditState(); }}>Cancel</Button>
                                        <Button onClick={savePricing} loading={saving} size="sm">Save Pricing</Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {/* BOL price */}
                                    {isOOS && estimate.estimated_weight_lbs > 0 && (
                                        <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <span className="text-xs font-bold text-blue-700">Est. Shipment Weight</span>
                                            <span className="text-sm font-bold text-blue-800">{Number(estimate.estimated_weight_lbs).toLocaleString()} lbs</span>
                                        </div>
                                    )}
                                    {isOOS && estimate.bol_price > 0 && (
                                        <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <span className="text-xs font-bold text-blue-700">BOL Move Price</span>
                                            <span className="text-sm font-bold text-blue-800">${Number(estimate.bol_price).toLocaleString()}</span>
                                        </div>
                                    )}
                                    {/* Weight entered but tariff not priced yet — the
                                        Building-status holding state for OOS. */}
                                    {isOOS && !(estimate.bol_price > 0) && (
                                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs font-semibold text-amber-700">
                                            Tariff not priced yet — add BOL Move Price via Edit, then move to Ready.
                                        </div>
                                    )}
                                    {estimate.bol_reference && (
                                        <div className="text-xs text-blue-500 px-3">BOL Ref: {estimate.bol_reference}</div>
                                    )}

                                    {/* Labor (local) — multi-day estimates carry per-day
                                        pricing on planned_phases (2026-06-09); render one
                                        line per priced day instead of the flat hours×rate. */}
                                    {!isOOS && pricedDayPhases.length > 0 ? (
                                        pricedDayPhases.map((p, i) => {
                                            const { min: minH, max: maxH, rate: baseRate } = phaseLaborPricing(p);
                                            // Per-day rates are stored as BASE rates; the stored
                                            // total prices every billed hour at base + extra-truck
                                            // surcharge (see ClientPricingTab dayLaborMin/Max).
                                            const rate = baseRate + estimateExtraTruckSurcharge(estimate, config);
                                            return (
                                                <div key={p.sequence || i} className="flex justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                    <div>
                                                        <span className="text-xs font-bold text-slate-600">Day {i + 1} Labor</span>
                                                        <span className="text-[10px] text-slate-400 ml-2">
                                                            {p.crew_size || '?'} crew · {minH}{maxH !== minH ? `–${maxH}` : ''} hrs × ${rate}/hr
                                                        </span>
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-700">
                                                        {minH !== maxH
                                                            ? `$${(minH * rate).toLocaleString()} – $${(maxH * rate).toLocaleString()}`
                                                            : `$${(maxH * rate).toLocaleString()}`}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    ) : !isOOS && (() => {
                                        // Effective rate (base + extra-truck surcharge) — matches
                                        // how the stored total was computed (QA M2 BUG-3).
                                        const effRate = estimateEffectiveRate(estimate, config);
                                        const surcharge = estimateExtraTruckSurcharge(estimate, config);
                                        return (
                                        <div className="flex justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                            <div>
                                                <span className="text-xs font-bold text-slate-600">Labor</span>
                                                <span className="text-[10px] text-slate-400 ml-2">
                                                    {hasRange ? `${estimate.min_hours}–${estimate.max_hours} hrs` : `${estimate.estimated_hours || 0} hrs`} × ${effRate}/hr
                                                    {surcharge > 0 && ` ($${estimate.hourly_rate || 0} + $${surcharge} extra trucks)`}
                                                </span>
                                            </div>
                                            <span className="text-sm font-bold text-slate-700">
                                                {hasRange
                                                    ? `$${(estimate.min_hours * effRate).toLocaleString()} – $${(estimate.max_hours * effRate).toLocaleString()}`
                                                    : `$${((estimate.estimated_hours || 0) * effRate).toLocaleString()}`
                                                }
                                            </span>
                                        </div>
                                        );
                                    })()}

                                    {/* Packing */}
                                    {packingMinDisplay > 0 && (
                                        <div className="flex justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                                            <div>
                                                <span className="text-xs font-bold text-amber-700">Packing</span>
                                                <span className="text-[10px] text-amber-500 ml-2">
                                                    {estimate.packing_crew_size} packers × {packingMinHrs}{hasPackingRange ? `–${packingMaxHrs}` : ''} hrs × ${estimate.packing_rate}/hr
                                                </span>
                                            </div>
                                            <span className="text-sm font-bold text-amber-800">
                                                {hasPackingRange
                                                    ? `$${packingMinDisplay.toLocaleString()} – $${packingMaxDisplay.toLocaleString()}`
                                                    : `$${packingMinDisplay.toLocaleString()}`
                                                }
                                            </span>
                                        </div>
                                    )}

                                    {/* Other fees */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {/* Travel/materials are LOCAL-only — never in the
                                            OOS tariff total (QA M2 BUG-4). */}
                                        {!isOOS && (estimate.travel_fee || 0) > 0 && (
                                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                                                <div className="text-[10px] text-slate-400 font-bold uppercase">Travel</div>
                                                <div className="text-sm font-bold text-slate-700">${Number(estimate.travel_fee).toLocaleString()}</div>
                                            </div>
                                        )}
                                        {!isOOS && (estimate.hold_fee || 0) > 0 && (
                                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                                                <div className="text-[10px] text-slate-400 font-bold uppercase">Hold</div>
                                                <div className="text-sm font-bold text-slate-700">${Number(estimate.hold_fee).toLocaleString()}</div>
                                            </div>
                                        )}
                                        {!isOOS && (estimate.materials_fee || 0) > 0 && (
                                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                                                <div className="text-[10px] text-slate-400 font-bold uppercase">Materials</div>
                                                <div className="text-sm font-bold text-slate-700">${Number(estimate.materials_fee).toLocaleString()}</div>
                                            </div>
                                        )}
                                        {heavyTotal > 0 && (
                                            <div className="bg-rose-50 rounded-lg p-3 border border-rose-200 text-center">
                                                <div className="text-[10px] text-rose-400 font-bold uppercase">Heavy Items ({estimate.heavy_item_count})</div>
                                                <div className="text-sm font-bold text-rose-700">${heavyTotal.toLocaleString()}</div>
                                            </div>
                                        )}
                                        {(estimate.removal_fee || 0) > 0 && (
                                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                                                <div className="text-[10px] text-slate-400 font-bold uppercase">Removal</div>
                                                <div className="text-sm font-bold text-slate-700">${Number(estimate.removal_fee).toLocaleString()}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!canViewPricing && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                            <p className="text-sm text-slate-400">You don't have permission to see pricing details.</p>
                        </div>
                    )}

                    {estimate.heavy_items && <div><span className="text-[10px] font-bold text-slate-400 uppercase block">Heavy Items Detail</span><span className="text-sm text-slate-700">{estimate.heavy_items}</span></div>}
                    {estimate.description && <div><span className="text-[10px] font-bold text-slate-400 uppercase block">Notes</span><span className="text-sm text-slate-700">{estimate.description}</span></div>}
                </div>

                {/* Activity feed — audit log of every change made to this
                    estimate (status, pricing, dates, etc.). Sourced from
                    audit_log via the Postgres trigger in
                    sql/030_audit_log.sql. */}
                {estimate?.id && (
                    <div className="px-6 pb-4">
                        <ActivityLog tableName="estimates" recordId={estimate.id} />
                    </div>
                )}

                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                    <Button variant="danger" onClick={() => onDelete(estimate.id)}>Delete Estimate</Button>
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </div>
            </div>

            {/* ===== NC LEGAL WARNING: Send Written Estimate confirmation ===== */}
            {writtenModal && (
                <div
                    className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => { e.stopPropagation(); if (!writtenLoading) setWrittenModal(false); }}
                >
                    <div
                        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="px-6 py-5 border-b border-slate-100 bg-amber-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-xl">!</div>
                                <div>
                                    <h3 className="text-lg font-bold text-amber-900">Send Written Estimate?</h3>
                                    <p className="text-xs text-amber-700 mt-0.5">Please read the NC legal note below before continuing.</p>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-3 text-sm text-slate-700">
                            <p>
                                <strong className="text-slate-900">Heads up:</strong> Under North Carolina moving regulations, once a <em>written estimate</em> is delivered to a customer, the pricing on that estimate is subject to restrictions on how much it can change before move day.
                            </p>
                            <p>
                                This link clearly labels the document as a <strong>non-binding ballpark</strong>, but sending it still time-stamps a "written estimate sent" event on this job. Only send it if you're sure you want that record.
                            </p>
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                                <div className="font-bold text-slate-600 uppercase tracking-wide mb-1.5">What happens next</div>
                                <ul className="space-y-1 text-slate-600 list-disc list-inside">
                                    <li>A hosted client-facing link is generated</li>
                                    <li>You'll get a URL to copy and send yourself (email, text, etc.)</li>
                                    <li>When the client opens it, we stamp "opened" so you can see it here</li>
                                    <li>The link shows ballpark ranges only — no internal pricing</li>
                                </ul>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setWrittenModal(false)}
                                disabled={writtenLoading}
                            >Cancel</Button>
                            <Button
                                onClick={async () => {
                                    await handleGenerateWrittenLink();
                                    setWrittenModal(false);
                                }}
                                loading={writtenLoading}
                            >
                                Yes, Generate Link
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Status-change confirmation dialog — rendered by the
                useEstimateStatusChange hook (portal-based, z-[300]). */}
            {statusChanger.dialog}
        </div>,
        document.body
    );
}
